import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { SharedModule } from '@shared/shared.module';
import { subDays } from 'date-fns';
import { EconomyMeasure } from '@src/app/features/dashboard/dashboard-management/pages/economy-history/economy-history.labels';

/** Atalho de período ou 'custom' (De/Até escolhidos no calendário). */
export type EconomyHistoryPeriodPreset = 7 | 30 | 90 | 'custom';

/** Período em YYYY-MM-DD, como a API espera. */
export interface EconomyHistoryPeriod {
  from: string;
  to: string;
}

/**
 * Filtros compactos do Histórico da economia (card 3323): atalhos de 7, 30 e 90 dias, período
 * livre (De/Até) e a régua. Toda mudança emite na hora: a página recarrega sem botão "Atualizar".
 */
@Component({
  selector: 'app-economy-history-filters',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './economy-history-filters.component.html',
  styleUrls: ['./economy-history-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EconomyHistoryFiltersComponent {
  private readonly dateFormatter = inject(DateFormatterService);

  readonly measure = input<EconomyMeasure>('inicial');
  readonly loading = input(false);

  readonly periodChange = output<EconomyHistoryPeriod>();
  readonly measureChange = output<EconomyMeasure>();
  readonly refresh = output<void>();

  readonly presets: { value: 7 | 30 | 90; label: string }[] = [
    { value: 7, label: '7 dias' },
    { value: 30, label: '30 dias' },
    { value: 90, label: '90 dias' },
  ];

  readonly preset = signal<EconomyHistoryPeriodPreset>(30);
  readonly showCustom = signal(false);

  /** Datas do período livre: `Date` ao abrir, string `YYYY-MM-DD` depois que o usuário mexe. */
  fromDate: Date | string = subDays(this.dateFormatter.getTodayDate(), 30);
  toDate: Date | string = this.dateFormatter.getTodayDate();

  /** Período atual, para a página carregar na abertura. */
  currentPeriod(): EconomyHistoryPeriod {
    return {
      from: this.dateFormatter.formatDateToYYYYMMDD(this.fromDate),
      to: this.dateFormatter.formatDateToYYYYMMDD(this.toDate),
    };
  }

  selectPreset(days: 7 | 30 | 90): void {
    this.preset.set(days);
    this.showCustom.set(false);
    const today = this.dateFormatter.getTodayDate();
    this.fromDate = subDays(today, days);
    this.toDate = today;
    this.periodChange.emit(this.currentPeriod());
  }

  toggleCustom(): void {
    this.showCustom.update(v => !v);
  }

  onCustomDateChange(): void {
    if (!this.fromDate || !this.toDate) return;
    this.preset.set('custom');
    this.periodChange.emit(this.currentPeriod());
  }

  /** "07/09 a 07/10/2026", rótulo do botão do período livre. */
  customLabel(): string {
    const from = this.dateFormatter.formatDateInFormat(this.fromDate, 'dd/MM');
    const to = this.dateFormatter.formatDateInFormat(this.toDate, 'dd/MM/yyyy');
    return `${from} a ${to}`;
  }

  setMeasure(measure: EconomyMeasure): void {
    if (measure === this.measure()) return;
    this.measureChange.emit(measure);
  }
}
